import { useMemo } from 'react';
import type { ExamScore } from '@/types';
import { buildExamComparison, buildScoreSummary, buildSubjectHistories } from '@/lib/scoreTrend';
import SubjectTrendChart from './SubjectTrendChart';
import ScoreCompareTable from './ScoreCompareTable';

const MAX_CHARTS = 6;

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export default function ScoreTrendPanel({ scores }: { scores: ExamScore[] }) {
  const histories = useMemo(() => buildSubjectHistories(scores), [scores]);
  const comparison = useMemo(() => buildExamComparison(scores), [scores]);
  const summary = useMemo(() => buildScoreSummary(histories), [histories]);

  if (histories.length === 0) return null;

  const shown = histories.slice(0, MAX_CHARTS);
  const hiddenCount = histories.length - shown.length;

  const subjectNames = (list: typeof histories) =>
    list.slice(0, 2).map((h) => h.subject).join('·');

  const parts: string[] = [];
  if (comparison?.avgDelta != null && comparison.avgDelta !== 0) {
    parts.push(
      comparison.avgDelta > 0
        ? `이번 시험 평균이 이전보다 ${comparison.avgDelta}점 올랐어요.`
        : `이번 시험 평균이 이전보다 ${Math.abs(comparison.avgDelta)}점 내렸어요.`,
    );
  } else if (comparison?.avgDelta === 0) {
    parts.push('이번 시험 평균은 이전과 비슷해요.');
  }
  if (summary.rising.length > 0) parts.push(`${subjectNames(summary.rising)}가 상승세예요.`);
  if (summary.falling.length > 0) parts.push(`${subjectNames(summary.falling)}는 관리가 필요해요.`);
  if (parts.length === 0) parts.push('기록이 쌓이면 과목별 변화를 분석해드려요.');
  const summaryText = parts.join(' ');

  const tiles = [
    {
      label: '평균 점수',
      value: summary.average ?? '—',
      unit: summary.average === null ? '' : '점',
      icon: 'ri-equalizer-line',
      tone: 'bg-secondary-100 text-secondary-900',
    },
    {
      label: '상승 과목',
      value: summary.rising.length,
      unit: '개',
      icon: 'ri-arrow-up-line',
      tone: 'bg-primary-100 text-primary-700',
    },
    {
      label: '하락 과목',
      value: summary.falling.length,
      unit: '개',
      icon: 'ri-arrow-down-line',
      tone: 'bg-accent-100 text-accent-800',
    },
    {
      label: '이전 대비',
      value:
        comparison?.avgDelta == null
          ? '—'
          : `${comparison.avgDelta > 0 ? '+' : ''}${round1(comparison.avgDelta)}`,
      unit: comparison?.avgDelta == null ? '' : '점',
      icon: 'ri-line-chart-line',
      tone: 'bg-background-200 text-foreground-700',
    },
  ];

  return (
    <div className="rounded-xl border border-background-200 bg-background-100 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-line-chart-line text-xs"></i>
            </span>
            성적 추이 분석
          </h3>
          <p className="mt-1 text-xs text-foreground-500">
            과목별 점수 변화와 이전 시험 대비 결과를 한눈에 봐요.
          </p>
        </div>
        {summary.bestSubject && (
          <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-semibold text-primary-700">
            <i className="ri-award-line"></i>
            최고점 {summary.bestSubject}
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-lg bg-background-50 px-3 py-2.5">
            <div className="flex items-center gap-1.5">
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-md text-[11px] ${t.tone}`}
              >
                <i className={t.icon}></i>
              </span>
              <span className="text-[11px] text-foreground-500">{t.label}</span>
            </div>
            <p className="mt-1 text-lg font-bold text-foreground-950">
              {t.value}
              <span className="ml-0.5 text-xs font-medium text-foreground-500">{t.unit}</span>
            </p>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-100/60 px-3 py-2.5">
        <i className="ri-magic-line mt-0.5 text-primary-600"></i>
        <p className="text-xs leading-relaxed text-foreground-800">{summaryText}</p>
      </div>

      <div className="mt-4">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground-700">
          <i className="ri-bar-chart-grouped-line text-primary-600"></i>
          과목별 점수 변화
          <span className="font-normal text-foreground-400">
            · 회차별 점수(막대 높이는 과목 내 상대 비교)
          </span>
        </p>
        <div className="flex flex-col gap-2">
          {shown.map((h) => (
            <SubjectTrendChart key={h.subject} history={h} />
          ))}
        </div>
        {hiddenCount > 0 && (
          <p className="mt-2 text-center text-[11px] text-foreground-400">
            외 {hiddenCount}개 과목
          </p>
        )}
      </div>

      <div className="mt-4">
        {comparison && <ScoreCompareTable comparison={comparison} />}
      </div>
    </div>
  );
}